import React from 'react';
import { useTranslation } from 'react-i18next';

// Hidden from sight but still read by assistive technology, so the button keeps its name.
const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

type Props = {
  loading: boolean;
  /** Shown while loading instead of the animated dots (for example a "Loading..." text). */
  loadingContent?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Button content that shows either its label or a loading indicator, keeping
 * both mounted and hiding the other, so the label's text node is never removed.
 * Browser page translation wraps text nodes in <font> elements; a label that
 * React removes and re-adds then no longer matches the DOM and the page crashes
 * (JAA-388).
 */
const LoadingLabel = ({ loading, loadingContent, children }: Props) => {
  const { t } = useTranslation();
  const hiddenWhenIdle = loading ? undefined : { display: 'none' };
  return (
    <>
      <span style={loading ? visuallyHidden : undefined}>{children}</span>
      {loadingContent === undefined ? (
        <>
          <span className="loading-dots" style={hiddenWhenIdle}>
            <span></span>
          </span>
          {/* Persistent live region: the dots carry no text, so announce the state here. */}
          <span role="status" style={visuallyHidden}>
            {loading ? t('building.offMarket.modal.loading', 'Loading...') : ''}
          </span>
        </>
      ) : (
        <span style={hiddenWhenIdle}>{loadingContent}</span>
      )}
    </>
  );
};

export default LoadingLabel;
